<template>
    <div class="np-card">
        <div class="np-header">
            <i class="fal fa-bell np-header-icon"></i>
            <h3 class="np-title">{{ $t('my_profile.notification_preferences') }}</h3>
        </div>
        <div class="np-table-wrap">
            <table class="np-table">
                <thead>
                    <tr>
                        <th class="np-th-event">{{ $t('my_profile.notif.event') }}</th>
                        <th class="np-th-channel">
                            <i class="fal fa-envelope"></i> Email
                        </th>
                        <th class="np-th-channel">
                            <i class="fal fa-bell"></i> Push
                        </th>
                        <th class="np-th-channel">
                            <i class="fab fa-whatsapp"></i> WhatsApp
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="event in events" :key="event.key">
                        <td class="np-event">{{ $t('my_profile.notif.events.' + event.key) }}</td>
                        <td
                            v-for="channel in channels"
                            :key="channel"
                            class="np-toggle-cell"
                        >
                            <label class="np-toggle">
                                <input
                                    type="checkbox"
                                    v-model="prefs[event.key][channel]"
                                    @change="onToggle(event.key, channel)"
                                />
                                <span class="np-toggle-slider"></span>
                            </label>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import axios from '@/config/backend'

const DEFAULT_CHANNELS = { email: false, push: false, whatsapp: false }

export default {
    name: 'NotificationPreferences',
    props: {
        userId: {
            type: [Number, String],
            required: true
        },
        preferences: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            saving: false,
            channels: ['email', 'push', 'whatsapp'],
            events: [
                { key: 'creation' },
                { key: 'assignment_change' },
                { key: 'citizen_message' },
                { key: 'internal_note' },
                { key: 'reminder' },
                { key: 'overdue' },
                { key: 'resolution' }
            ],
            prefs: {}
        }
    },
    computed: {
        baseURL() {
            return this.$store.state.baseURL
        }
    },
    created() {
        this.initPrefs()
    },
    watch: {
        preferences() {
            this.initPrefs()
        }
    },
    methods: {
        initPrefs() {
            this.events.forEach((e) => {
                this.$set(this.prefs, e.key, { ...DEFAULT_CHANNELS })
            })
            if (this.preferences && this.preferences.length) {
                this.preferences.forEach((pref) => {
                    if (this.prefs[pref.event]) {
                        this.$set(this.prefs, pref.event, {
                            ...DEFAULT_CHANNELS,
                            ...pref.channels
                        })
                    }
                })
            }
        },
        onToggle() {
            this.savePreferences()
        },
        savePreferences() {
            this.saving = true
            const preferences = this.events.map((e) => ({
                event: e.key,
                channels: { ...this.prefs[e.key] }
            }))
            axios
                .put(`${this.baseURL}/notification-preferences/${this.userId}`, { preferences })
                .then(() => {
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('my_profile.saved')
                    })
                })
                .catch(() => {
                    this.$store.dispatch('toast', {
                        type: 'error',
                        title: 'Erreur'
                    })
                })
                .finally(() => {
                    this.saving = false
                })
        }
    }
}
</script>

<style scoped>
.np-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.np-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    border-bottom: 1px solid #f1f5f9;
}

.np-header-icon {
    font-size: 1rem;
    color: #3b82f6;
    flex-shrink: 0;
}

.np-title {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

.np-table-wrap {
    overflow-x: auto;
}

.np-table {
    width: 100%;
    border-collapse: collapse;
}

.np-th-event {
    text-align: left;
    padding: 12px 24px;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
    background: #f8fafc;
}

.np-th-channel {
    text-align: center;
    padding: 12px 16px;
    font-size: 0.75rem;
    font-weight: 700;
    color: #64748b;
    background: #f8fafc;
    white-space: nowrap;
}

.np-th-channel i {
    margin-right: 4px;
    font-size: 0.8rem;
}

.np-event {
    padding: 14px 24px;
    font-size: 0.84rem;
    font-weight: 500;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
}

.np-toggle-cell {
    text-align: center;
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
}

tr:last-child .np-event,
tr:last-child .np-toggle-cell {
    border-bottom: none;
}

/* Toggle switch */
.np-toggle {
    position: relative;
    display: inline-block;
    width: 38px;
    height: 22px;
    cursor: pointer;
}

.np-toggle input {
    opacity: 0;
    width: 0;
    height: 0;
}

.np-toggle-slider {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #e2e8f0;
    border-radius: 22px;
    transition: all 0.25s ease;
}

.np-toggle-slider::before {
    content: '';
    position: absolute;
    height: 16px;
    width: 16px;
    left: 3px;
    bottom: 3px;
    background: #fff;
    border-radius: 50%;
    transition: all 0.25s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.np-toggle input:checked + .np-toggle-slider {
    background: #0891B2;
}

.np-toggle input:checked + .np-toggle-slider::before {
    transform: translateX(16px);
}

.np-toggle:hover .np-toggle-slider {
    background: #cbd5e1;
}

.np-toggle:hover input:checked + .np-toggle-slider {
    background: #0e7490;
}

/* Responsive */
@media (max-width: 576px) {
    .np-th-event,
    .np-event {
        padding: 10px 14px;
    }

    .np-th-channel,
    .np-toggle-cell {
        padding: 10px 8px;
    }

    .np-th-channel {
        font-size: 0.68rem;
    }
}
</style>
